/**
 * Mirrors Better Auth's session into the Zustand session-store so all the
 * existing components that read `state.user` keep working without per-component
 * refactors. Mount once at the root.
 *
 * Dev note: in tests + Vitest, MSW handlers for /v1/auth/* return canned
 * sessions and this component drives the store the same way it does in prod.
 */

import { useEffect } from 'react';
import { useSessionStore } from '../stores/session-store.ts';
import { authClient } from './auth-client.ts';

/**
 * One-directional sync: when Better Auth reports a session, mirror it into the
 * Zustand store so all the legacy `state.user` consumers light up.
 *
 * We deliberately do NOT clear the store when BA reports no session. The
 * legacy GuestSavePrompt + tests rely on optimistic local sign-in; clearing
 * here would race them. Real sign-out clears the store via the caller.
 */
export function AuthSync() {
  const { data: session, isPending } = authClient.useSession();
  const storeUser = useSessionStore((s) => s.user);
  const storeSignIn = useSessionStore((s) => s.signIn);

  useEffect(() => {
    if (isPending) return;
    const sessionUser = session?.user;
    if (!sessionUser) return;
    const nextUser = { email: sessionUser.email ?? '(guest)' };
    if (storeUser?.email !== nextUser.email) {
      storeSignIn(nextUser);
    }
  }, [session, isPending, storeUser, storeSignIn]);

  return null;
}
